// AppNav — shared navigation chrome for the two shells (AppShell and
// AdminShell). Provides two data-driven primitives so each shell only
// has to pass an array of items; the M3 visual treatment (animated
// indicator, badges, layoutId, locked styling) lives here, once.
//
// Visibility is fixed by the primitives:
//   - <BottomNavBar>  : fixed, mobile-only (`lg:hidden`)
//   - <SideRail>      : sticky, desktop-only (`hidden lg:flex`)
//
// Consumers decide which items appear; both shells therefore share
// exactly the same look and motion vocabulary.
//
// `NavItem` is the shape both shells already use internally; making it
// a public type keeps the bar/rail "just an array of <button>s" from
// the caller's point of view.

import type { ReactNode } from "react";
import { motion } from "motion/react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { BrandMark } from "./BrandMark";

/* ─────────────────────  Public types  ───────────────────── */

export type NavItem<TId extends string = string> = {
  id: TId;
  label: string;
  icon: LucideIcon;
  badge?: string;
  locked?: boolean;
};

/* Shared layout for every nav button (mobile item, mobile "More",
 * and the rail item). Keeping it as a constant ensures all three
 * stay visually aligned — e.g. the 56×64 hit target is identical
 * across the bottom nav and the rail item hit-targets. */
const NAV_BUTTON_BASE =
  "flex min-h-[56px] min-w-[64px] flex-col items-center justify-center gap-0.5 px-2 transition-colors";

/* ─────────────────────  Bottom nav bar  ─────────────────────
 *
 * Fixed bottom nav (M3). Items are auto-distributed in a single grid
 * row; supply `overflow` to append a trailing "More" button after the
 * items (AppShell uses it; AdminShell omits it).
 *
 * The `layoutId` should be unique per bar so two bars on the same
 * page don't share the spring animation state.
 */
export function BottomNavBar<TId extends string>({
  items,
  activeId,
  onSelect,
  overflow,
  ariaLabel = "底部主導覽",
  layoutId = "bottomNavIndicator",
}: {
  items: NavItem<TId>[];
  activeId: TId | null;
  onSelect: (id: TId) => void;
  /** Optional trailing "More" button. `active` is consumer-controlled
   *  so a shell can show the highlight when a hidden item is selected. */
  overflow?: {
    icon: LucideIcon;
    label: string;
    onClick: () => void;
    active?: boolean;
  };
  ariaLabel?: string;
  layoutId?: string;
}) {
  return (
    <nav
      aria-label={ariaLabel}
      data-bottom-nav
      className="fixed inset-x-0 bottom-0 z-30 border-t border-border/60 bg-background/70 backdrop-blur-lg lg:hidden"
    >
      <ul
        className="grid pb-safe py-2"
        style={{
          gridTemplateColumns: `repeat(${items.length + (overflow ? 1 : 0)}, minmax(0, 1fr))`,
        }}
      >
        {items.map((item) => (
          <BottomNavItem
            key={item.id}
            item={item}
            active={activeId === item.id}
            onClick={() => onSelect(item.id)}
            layoutId={layoutId}
          />
        ))}
        {overflow && (
          <li className="flex items-stretch justify-center">
            <button
              type="button"
              aria-label={overflow.label}
              onClick={overflow.onClick}
              className={cn(
                NAV_BUTTON_BASE,
                "text-foreground/70",
                overflow.active && "text-primary",
              )}
            >
              <overflow.icon className="h-6 w-6" aria-hidden />
              <span className="text-[11px] font-medium">{overflow.label}</span>
            </button>
          </li>
        )}
      </ul>
    </nav>
  );
}

/* ─────────────────────  Bottom nav item  ───────────────────── */

function BottomNavItem<TId extends string>({
  item,
  active,
  onClick,
  layoutId,
}: {
  item: NavItem<TId>;
  active: boolean;
  onClick: () => void;
  layoutId: string;
}) {
  const Icon = item.icon;
  return (
    <li className="flex items-stretch justify-center">
      <button
        type="button"
        onClick={onClick}
        aria-current={active || undefined}
        className={cn(
          NAV_BUTTON_BASE,
          "group relative",
          active ? "text-primary" : "text-foreground/70",
        )}
      >
        {active && (
          <motion.span
            layoutId={layoutId}
            aria-hidden
            className="absolute top-0 h-14 w-14 rounded-full bg-primary/15"
            transition={{ type: "spring", stiffness: 380, damping: 30 }}
          />
        )}
        <motion.span
          className="relative"
          animate={active ? { y: 0, scale: 1.08 } : { y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 420, damping: 24 }}
        >
          <Icon className="h-6 w-6" aria-hidden />
          {item.badge && (
            <motion.span
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 22 }}
              className={cn(
                "absolute -right-2 -top-1 rounded-full px-1.5 text-[10px] font-semibold leading-tight ring-2 ring-background",
                item.badge === "新"
                  ? "bg-accent text-accent-foreground"
                  : "bg-primary text-primary-foreground",
              )}
            >
              {item.badge}
            </motion.span>
          )}
        </motion.span>
        <span className="relative text-[11px] font-medium">{item.label}</span>
      </button>
    </li>
  );
}

/* ─────────────────────  Side rail (desktop)  ─────────────────────
 *
 * Sticky left rail on `lg+`. The body content supplies the matching
 * `lg:ml-20 lg:px-8` offset (we don't render the rail as fixed so it
 * flows inside the shell's flex/grid).
 *
 * Slots:
 *   - `brand`     : element rendered at the top of the rail (default
 *                   is the BrandMark link to "/")
 *   - `items`     : vertical stack of NavItem buttons
 *   - `footer`    : element rendered at the bottom of the rail
 *                   (settings button in AppShell, user card in AdminShell)
 *   - `ariaLabel` : accessible name for the inner <nav>
 */
export function SideRail<TId extends string>({
  items,
  activeId,
  onSelect,
  brand,
  footer,
  ariaLabel = "主導覽",
}: {
  items: NavItem<TId>[];
  activeId: TId | null;
  onSelect: (id: TId) => void;
  brand?: ReactNode;
  footer?: ReactNode;
  ariaLabel?: string;
}) {
  return (
    <aside
      data-side-rail
      className="sticky top-0 z-30 hidden h-screen w-20 border-r border-border/60 bg-surface-container-lowest lg:flex lg:flex-col lg:items-center lg:py-4"
    >
      {brand ?? (
        <a
          href="/"
          className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-2xl"
        >
          <BrandMark size="md" />
        </a>
      )}
      <nav
        aria-label={ariaLabel}
        className="flex flex-1 flex-col items-center gap-2"
      >
        {items.map((item) => (
          <RailItem
            key={item.id}
            item={item}
            active={activeId === item.id}
            onClick={() => onSelect(item.id)}
          />
        ))}
      </nav>
      {footer && (
        <div className="mt-2 flex flex-col items-center gap-1">{footer}</div>
      )}
    </aside>
  );
}

/* The active rail item uses the same primary-tinted pill as the rest
 * of the M3 navigation, but stays a 14×14 square (no label) since
 * width is fixed at 80px (w-20). */
function RailItem<TId extends string>({
  item,
  active,
  onClick,
}: {
  item: NavItem<TId>;
  active: boolean;
  onClick: () => void;
}) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active || undefined}
      aria-label={item.label}
      className={cn(
        "relative inline-flex h-14 w-14 items-center justify-center rounded-2xl text-foreground/70 transition-colors",
        active ? "bg-primary/15 text-primary" : "hover:bg-secondary",
      )}
    >
      <Icon className="h-6 w-6" aria-hidden />
      {item.badge && (
        <span className="absolute right-1 top-1 rounded-full bg-primary px-1.5 text-[10px] font-semibold leading-tight text-primary-foreground">
          {item.badge}
        </span>
      )}
    </button>
  );
}

/* ─────────────────────  AppBar (mobile top bar)  ─────────────────────
 *
 * Optional companion to BottomNavBar. Renders the same sticky top bar
 * AppShell uses (left menu trigger + brand + right actions). Kept
 * here so AdminShell can opt-in to the same look if/when it needs a
 * mobile top action.
 */
export function AppBar({
  brand,
  leading,
  trailing,
}: {
  brand: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <header
      data-top-bar
      className="sticky top-0 z-30 border-b border-border/60 bg-background/90 pt-safe backdrop-blur lg:hidden"
    >
      <div className="flex h-14 items-center gap-2 px-3 sm:px-4">
        {leading}
        {brand}
        {trailing && (
          <div className="ml-auto flex items-center gap-0.5">{trailing}</div>
        )}
      </div>
    </header>
  );
}
